<template>
  <div class="tw:mt-8">
    <div class="tw:flex tw:items-center tw:py-1">
      <p class="tw:text-2xl tw:font-bold">{{ $t('ri_market_position_header') }}</p>
    </div>
    <p class="tw:text-base tw:font-normal tw:mb-6">{{ $t('ri_market_position_subtext') }}</p>

    <div
      v-if="hasLowMarketData || !status"
      class="tw:rounded-lg tw:py-3 tw:px-4 tw:flex tw:items-start tw:mb-6 tw:bg-white tw:border tw:border-gray-300"
    >
      <img :src="alertIcon" alt="" class="tw:mr-3 tw:shrink-0 tw:w-6 tw:h-10" />
      <div>
        <p class="tw:font-semibold tw:text-md">{{ $t('ri_market_overview_low') }}</p>
        <p class="tw:text-sm tw:mb-2">{{ $t('ri_market_overview_info') }}</p>
        <p class="tw:text-sm" v-html="$t('ri_market_overview_low_nba')" />
      </div>
    </div>

    <template v-if="status">
      <div class="tw:rounded-lg tw:py-3 tw:px-4 tw:flex tw:items-center tw:mb-6" :class="statusBannerClass">
        <img v-if="statusIcon" :src="statusIcon" alt="" class="tw:mr-3 tw:shrink-0 tw:w-6 tw:h-6" />
        <div>
          <p class="tw:font-semibold">{{ statusTitle }}</p>
          <p class="tw:text-sm">{{ statusDescription }}</p>
        </div>
      </div>

      <div class="tw:grid tw:grid-cols-1 tw:sm:grid-cols-3 tw:gap-3">
        <div class="tw:bg-white tw:border tw:border-gray-300 tw:rounded-lg tw:px-5 tw:py-4">
          <div class="tw:flex tw:items-center tw:mb-1">
            <p class="tw:text-sm tw:font-bold tw:text-[#0A1121]">{{ $t('ri_hotel_avg_rate') }}</p>
          </div>
          <p v-if="yourAvgRate !== null" class="tw:text-[32px] tw:font-normal" :class="avgRateColorClass">
            {{ formattedYourAvgRate }}
          </p>
          <p v-else class="tw:text-[32px] tw:font-normal tw:text-gray-400">—</p>
          <p class="tw:text-xs tw:text-gray-800 tw:mt-1">{{ $t('ri_rate_per_night') }}</p>
        </div>

        <div class="tw:bg-white tw:border tw:border-gray-300 tw:rounded-lg tw:px-5 tw:py-4">
          <div class="tw:flex tw:items-center tw:mb-1">
            <p class="tw:text-sm tw:font-bold tw:text-[#0A1121]">{{ $t('ri_market_average') }}</p>
          </div>
          <p v-if="marketAvg !== null" class="tw:text-[32px] tw:font-normal">
            {{ formattedMarketAvg }}
          </p>
          <p v-else class="tw:text-[32px] tw:font-normal tw:text-gray-400">—</p>
          <p class="tw:text-xs tw:text-gray-800 tw:mt-1">
            {{ $t('ri_market_average_info') }}
          </p>
        </div>

        <div class="tw:bg-white tw:border tw:border-gray-300 tw:rounded-lg tw:px-5 tw:py-4">
          <div class="tw:flex tw:items-center tw:mb-1">
            <p class="tw:text-sm tw:font-bold tw:text-[#0A1121]">{{ $t('ri_rate_difference') }}</p>
          </div>
          <p v-if="priceDiff !== null" class="tw:text-[32px] tw:font-normal">
            {{ formattedPriceDiff }}
          </p>
          <p v-else class="tw:text-[32px] tw:font-normal tw:text-gray-400">—</p>
          <p v-if="priceDiffPct !== null" class="tw:text-xs tw:text-gray-800 tw:mt-1">
            {{ $t('ri_rate_difference_info', { Percentage: `${priceDiffPct}%` }) }}
          </p>
          <p v-else class="tw:text-xs tw:text-gray-800 tw:mt-1">—</p>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useFormatting } from '@/composables/useFormatting'
import alertIcon from '@/assets/img/general/alert-icon.svg'
import competitiveIcon from '@/assets/img/rate-insights/thumb-icon.svg'
import aboveAverageIcon from '@/assets/img/rate-insights/above-average-icon.svg'
import belowAverageIcon from '@/assets/img/rate-insights/below-average-icon.svg'

const props = defineProps({
  yourAvgRate: {
    type: Number,
    default: null,
  },
  marketAvg: {
    type: Number,
    default: null,
  },
  priceDiff: {
    type: Number,
    default: null,
  },
  priceDiffPct: {
    type: Number,
    default: null,
  },
  status: {
    type: String,
    default: null,
    validator: v => ['well_positioned', 'above_market', 'below_market'].includes(v),
  },
  hasLowMarketData: {
    type: Boolean,
    default: false,
  },
})

const { t } = useI18n()
const { formatCurrency } = useFormatting()

const fmt = value => formatCurrency(value, { minimumFractionDigits: 0, maximumFractionDigits: 0 })

const formattedYourAvgRate = computed(() => (props.yourAvgRate !== null ? fmt(props.yourAvgRate) : null))
const formattedMarketAvg = computed(() => (props.marketAvg !== null ? fmt(props.marketAvg) : null))
const formattedPriceDiff = computed(() => {
  if (props.priceDiff === null) return null
  const abs = fmt(Math.abs(props.priceDiff))
  return props.priceDiff >= 0 ? `+${abs}` : `-${abs}`
})

const STATUS_CONFIG = {
  well_positioned: {
    bannerClass: 'tw:bg-blue-200 tw:border tw:border-blue-300',
    avgRateClass: 'tw:text-green-700',
    icon: competitiveIcon,
    titleKey: 'ri_position_good_header',
    descriptionKey: 'ri_position_good_subtext',
  },
  above_market: {
    bannerClass: 'tw:bg-red-100 tw:border tw:border-red-300',
    avgRateClass: 'tw:text-red-700',
    icon: aboveAverageIcon,
    titleKey: 'ri_market_average_high',
    descriptionKey: 'ri_market_average_high_info',
  },
  below_market: {
    bannerClass: 'tw:bg-orange-100 tw:border tw:border-orange-300',
    avgRateClass: 'tw:text-gray-800',
    icon: belowAverageIcon,
    titleKey: 'ri_market_average_below',
    descriptionKey: 'ri_market_average_below_info',
  },
}

const currentStatusConfig = computed(() => (props.status ? STATUS_CONFIG[props.status] : null))

const statusBannerClass = computed(() => currentStatusConfig.value?.bannerClass ?? '')
const avgRateColorClass = computed(() => currentStatusConfig.value?.avgRateClass ?? '')
const statusIcon = computed(() => currentStatusConfig.value?.icon ?? null)
const statusTitle = computed(() => (currentStatusConfig.value ? t(currentStatusConfig.value.titleKey) : ''))
const statusDescription = computed(() => (currentStatusConfig.value ? t(currentStatusConfig.value.descriptionKey) : ''))
</script>
